'use client';

import { type ReactElement, useState } from 'react';
import { type SetRequired } from 'type-fest';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { HStack } from '@ironhack/design-system2/components/stack';

import { type DatoCompany, type DatoStory } from '@/lib/datocms';
import { CancelWrap } from '@/app/components/server/cancel-wrap';
import { StoryPlayer } from '@/app/components/client/story-player';
import { StoryThumbnail } from '@/app/components/client/story-thumbnail';

type Story = Omit<DatoStory, 'company'> & {
  company: SetRequired<DatoCompany, 'horizontalBackgroundLogo'>;
};

type Props = {
  stories: Story[];
  onStoryView: (userName: string) => void;
} & FlexProps;

export const StoryList = (props: Props): ReactElement => {
  const { stories, onStoryView, ...flexProps } = props;
  const [currentStoryIndex, setCurrentStoryIndex] = useState<number>(-1);

  const handlePreviousStory = () => {
    setCurrentStoryIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? prevIndex : prevIndex - 1;
      if (newIndex !== prevIndex) onStoryView(stories[newIndex].userName);
      return newIndex;
    });
  };

  const handleNextStory = () => {
    setCurrentStoryIndex((prevIndex) => {
      const newIndex =
        prevIndex === stories.length - 1 ? prevIndex : prevIndex + 1;
      if (newIndex !== prevIndex) onStoryView(stories[newIndex].userName);
      return newIndex;
    });
  };

  const handleCloseStory = () => {
    setCurrentStoryIndex(-1);
  };

  return (
    <>
      <CancelWrap
        alignSelf="normal"
        as={Flex}
        below="md"
        justifyContent={[null, null, 'center']}
        withPadding
        {...flexProps}
      >
        <HStack alignItems="flex-start" as="ul" justify="center" spacing={1}>
          {stories.map((story, index) => (
            <StoryThumbnail
              as="li"
              cursor="pointer"
              key={`${story.userName}-${index}`}
              onClick={() => {
                setCurrentStoryIndex(index);
                onStoryView(stories[index].userName);
              }}
              story={story}
            />
          ))}
        </HStack>
      </CancelWrap>
      {currentStoryIndex >= 0 && (
        <StoryPlayer
          onExit={handleCloseStory}
          {...(currentStoryIndex < stories.length - 1 && {
            onNext: handleNextStory,
          })}
          {...(currentStoryIndex > 0 && { onPrevious: handlePreviousStory })}
          story={stories[currentStoryIndex]}
        />
      )}
    </>
  );
};
